import React from 'react';

import {buildActivityLine} from "../../../../Utils/Activity/buildActivityLine";
import {resolveActivityActor} from "../../../../Utils/Activity/resolveActivityActor";
import {usePrimeCache} from "../../../../Hooks/ServiceHooks";

/**
 * The one description component. It replaces a bespoke sentence renderer per activity type with a
 * registry entry per activity type: the entry supplies facts, the grammar decides the wording, and
 * this renders it.
 *
 * A supplier may return null to mean "not renderable yet" — the relationship-name lookups do this
 * while their cache primes. That matches what the components it replaces did, so a cache miss keeps
 * rendering nothing rather than a half-built sentence.
 *
 * System types never name an actor regardless of what the row carries, because a Fulcrum sync or an
 * expiry warning has no person behind it.
 */
export const ActivityLineDescription = props => {
    const {CustomEventData, Event, Entry} = props;

    // Primed before the supplier runs so it can read a relationship or permit type out of the cache
    // synchronously and answer, from the facts, whether the line is renderable yet. Unconditional
    // and ahead of every early return, because it is a hook; empty for the types that declare none.
    usePrimeCache(Entry?.services ?? []);

    const facts = Entry?.facts?.({data: CustomEventData ?? {}, event: Event});
    if (!facts) {
        return null;
    }

    const {subject, copula, verb, suffix, aside, trailer, actor} = buildActivityLine({
        actor: Entry.isSystem ? null : resolveActivityActor(Event),
        ...facts,
    });

    // suffix, aside and trailer render as fragments, not interpolated strings, because a supplier
    // may return nodes for them — sync_sample_event keeps a link to the originating Fulcrum record
    // inside its suffix. The actor is always a plain name.
    return <p>
        {subject} {copula} {verb}{suffix ? <> {suffix}</> : null}{actor ? ` by ${actor}` : null}{aside}.
        {trailer ? <> {trailer}</> : null}
    </p>;
};
